import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { PracticeWorkingHour } from '@/types/settings-api';

/**
 * Create a new working-hours profile for a practice (PROD-758).
 *   POST /api/Practice/AddWorkingHours
 *
 * Flutter parity: `addPracticeWorkingHours` in
 * `settings_data_source_impl.dart:1028`. Body shape verified against
 * working dev cURL 2026-05-13.
 *
 * Wire-shape notes (do NOT regress):
 *   - Top-level key is `practiceId` (NOT `practiceSetupId`).
 *   - `workingHourId` is OMITTED on create — the backend allocates it.
 *     (Flutter `addPracticeWorkingHours` strips it via removeWhere.)
 *   - `practiceName` is also OMITTED on create — server-derived from
 *     `practiceId`.
 *   - `organizationId` / `organizationTimezone` ship as `null` —
 *     backend fills from session.
 *   - Per-slot `id` ships as `""` (empty string) for new slots.
 *   - Per-slot `startHour` / `endHour` are HUMAN-FORMAT strings like
 *     `"04 AM"`, `"11 PM"`. NOT 24h numeric.
 *   - Per-slot `startMinute` / `endMinute` carry the `" min"` suffix
 *     like `"00 min"`. NOT bare digits.
 *   - `startDate` / `endDate` empty strings for permanent profiles.
 *
 * Caller is responsible for shaping the payload correctly — see the
 * `buildAddWorkingHoursPayload` helper in `working-hours-helpers.ts`.
 */
export function useAddWorkingHours(practiceSetupId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profile: PracticeWorkingHour) => {
      // Map the React-side `practiceSetupId` field to the wire-side
      // `practiceId` key. Strip workingHourId + practiceName per the
      // Flutter `addPracticeWorkingHours` (it does `removeWhere`).
      const { workingHourId: _whid, practiceName: _pn, practiceSetupId: _psid, ...rest } = profile;
      void _whid; void _pn; void _psid;
      const body = {
        ...rest,
        practiceId: practiceSetupId,
      };
      return post<unknown>('/api/Practice/AddWorkingHours', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'working-hours-list', practiceSetupId],
      });
    },
  });
}
